import { Component, EventEmitter, Input, Output } from "@angular/core";
import { PatientEhrOrderModel, PatientOrderResponseModel } from "../../models/patient.model";
import * as _ from "lodash";
import { defaultSelectOptions } from "src/app/components/default-options";
import { RecipeBase, RecipeData } from "src/app/models/recipe.model";
import { PatientService } from "src/app/services/patient.service";
import { FeedPatientListItem } from "src/app/pages/shared/administer-feed/feed-patient/list-item";
import { assertExhaustive } from "src/app/app.enums";
import { IAdditive } from "../../models/additive.model";
import { displayDate } from "../../app.util";
import {
	createContentProductsList,
	hasMissingValue,
	validateContentProducts,
	getStandingStatus,
	getOrderDetails,
	isOrderCalorieMatch,
} from "../../utils/orders.util";
import { Product } from "src/app/models/product.model";

@Component({
	selector: "app-order-select",
	templateUrl: "./order-select.component.html",
	styleUrls: ["./order-select.component.scss"],
})
export class OrderSelectComponent {
	@Input() selectedCalorie: number = null;
	@Input() orders: PatientEhrOrderModel[];
	@Input() orderObject: PatientOrderResponseModel;
	@Input() selectedOrder: PatientEhrOrderModel;
	@Input() scannedBase: RecipeBase;
	@Input() recipeData: RecipeData;
	@Output() selectedOrderChange = new EventEmitter<PatientEhrOrderModel>();
	@Output() validationResults2Change = new EventEmitter<
		IOrderValidationResult2[]
	>();
	@Output() changeOrder = new EventEmitter<void>();
	@Input() showChangeOrder = false;

	getStandingStatus = getStandingStatus;
	getOrderDetails = getOrderDetails;
	displayDate = displayDate;

	_validationResults2: IOrderValidationResult2[];

	get hasActiveNPOOrder() {
		return this.orderObject?.hasActiveNPOOrder;
	}

	/**
	 * @deprecated this doesn't really do anything
	 */
	get validationResults2() {
		return this._validationResults2;
	}

	/**
	 * @deprecated this doesn't really do anything because the parent component doesnt
	 * do anything with the validation results. Use `_validationResults2` instead.
	 */
	set validationResults2(value: IOrderValidationResult2[]) {
		this._validationResults2 = value;
		this.validationResults2Change.emit(this.validationResults2);
		console.log(
			`validationResults = ${JSON.stringify(this.validationResults2, null, 2)}`
		);
	}

	defaultSelectOptions = defaultSelectOptions;

	get hasChangeOrderListener(): boolean {
		return this.changeOrder.observed;
	}

	constructor(public patientService: PatientService) {}

	selectOrder(ev, order: PatientEhrOrderModel) {
		ev.stopPropagation();
		this.selectedOrder = order;
		this.selectedOrderChange.emit(this.selectedOrder);
	}

	hasActiveOrders(orders: PatientEhrOrderModel[]) {
		return orders?.length > 0;
	}

	// Validation Result helpers
	isValidOrderMatch(results: IOrderValidationResult2[]) {
		return !!results?.every((v) => v.isValid);
	}

	hasValidOrderFulfillment(results: IOrderValidationResult2[]) {
		return !!results?.some((v) => v.anyOrderFulfillmentValid);
	}

	hasCalorieMismatch(results: IOrderValidationResult2[]) {
		return !!results?.some((v) => !v.caloriesMatch);
	}

	isCalorieMatch() {
		if (!this.selectedCalorie) {
			// Avoid displaying mismatch warning if calorie selection has not been made yet
			return true;
		}

		return (
			isOrderCalorieMatch(this.selectedOrder, this.selectedCalorie) ||
			this.orders.length === 0
		);
	}

	// Validation

	/**
	 * Validates scanned contents (milk bottle contents + product ingredients)
	 * against the selected order's fulfillments without requiring calorie density.
	 * Used by the scan page and calculator on load to show immediate feedback.
	 */
	validateScannedProducts(products: IContentProduct[]) {
		if (!this.selectedOrder || !products.length) {
			this._validationResults2 = null;
			return;
		}

		const results = this.selectedOrder.fulfillments.map((fulfillment) =>
			validateContentProducts(products, fulfillment, this.selectedOrder.base)
		);
		const hasValidFulfillment = results.some((v) => v.isValid);

		this._validationResults2 = [
			{
				milkBottleId: null,
				isValid: hasValidFulfillment,
				anyOrderFulfillmentValid: hasValidFulfillment,
				caloriesMatch: true,
				validationResultsByOrderFulfillment: results,
			},
		];
	}

	validateOrderMatchesFeedPatientListItems(
		feedPatientListItems: FeedPatientListItem[]
	) {
		this.validationResults2 =
			this.validateSelectedOrderMatchesFeedPatientListItems(
				this.selectedOrder,
				feedPatientListItems
			);
	}

	/**
	 * This is called by the parent class. When calorie density, additives, and
	 * base are set, this will validate the values against the selected order.
	 *
	 * If any of the params are null, return an empty array meaning that
	 * validation doesn't happen.
	 */
	validateOrderMatchesRecipeCalculation(params: {
		calorieDensity: number;
		additives: IAdditive[];
		base: RecipeBase;
	}) {
		if (
			!hasMissingValue(
				this.selectedOrder,
				params.calorieDensity,
				params.additives
			)
		) {
			this.validationResults2 = [];
		}

		this.validationResults2 = this._validateOrderMatchesRecipeCalculation(
			this.selectedOrder,
			params.calorieDensity,
			params.additives,
			params.base
		);
	}

	// TODO: how is this different?
	validateSelectedOrderMatchesManualPrep(params: {
		calorieDensity: number;
		products: { productId: Product["id"]; productName: Product["name"] }[];
		recipeBases: RecipeBase[];
	}) {
		if (!params.calorieDensity || params.products.length === 0) {
			console.warn(
				// biome-ignore lint/nursery/noSecrets: <explanation>
				"validateSelectedOrderMatchesManualPrep: params are null"
			);
			return;
		}

		// TODO: refactor this using the util functions (below)
		const selectedOrder = this.selectedOrder;

		// include all recipe bases in products so they're included in the validation
		const baseProducts = params.recipeBases.map((base) => ({
			productId: base.baseProductId,
			productName: base.baseName,
		}));

		const products = params.products
			.filter((a) => a.productId)
			.map((a) => ({
				productId: a.productId,
				productName: a.productName,
			}))
			.concat(baseProducts);

		const resultsByOrderFulfillment =
			this.validateSelectedOrderMatchesProducts(
				products,
				this.selectedOrder
			);
		const anyOrderFulfillmentValid = resultsByOrderFulfillment.some(
			(orderFulfillment) => orderFulfillment.isValid
		);
		const caloriesMatch = isOrderCalorieMatch(selectedOrder, params.calorieDensity);

		this.validationResults2 = [
			{
				milkBottleId: null,
				isValid: anyOrderFulfillmentValid && caloriesMatch,
				anyOrderFulfillmentValid,
				caloriesMatch,
				validationResultsByOrderFulfillment: resultsByOrderFulfillment,
			},
		];
	}

	// TODO: how is this different?
	validateSelectedOrderMatchesFeedPatientListItems(
		selectedPatientEhrOrder: PatientEhrOrderModel,
		feedPatientListItems: FeedPatientListItem[]
	): IOrderValidationResult2[] {
		// It's possible that the patient does not have an order.
		if (!selectedPatientEhrOrder) {
			return;
		}

		const resultsByFeedPatientListItem: IOrderValidationResult2[] =
			feedPatientListItems.map((item) => {
				// pull products and calories out of the FeedPatientListItem
				let products: Array<{
					productId: Product["id"];
					productName: Product["name"];
				}>;
				let calorie: number;

				switch (item.type) {
					case "milkbottle":
						products = item.milkBottle.contents.map((c) => ({
							productId: c.contentProductId,
							productName: c.contentProductName,
						}));
						calorie = item.milkBottle.calorieObject?.caloriesOZ ?? 0;
						break;
					case "product":
						products = [
							{
								productId: item.product.id,
								productName: item.product.name,
							},
						];
						calorie = item.product.calorieObject?.caloriesOZ ?? item.product.calorieDensity;
						break;
					default:
						assertExhaustive(item);
				}

				const resultsByOrderFulfillment =
					this.validateSelectedOrderMatchesProducts(
						products,
						selectedPatientEhrOrder
					);
				const anyOrderFulfillmentValid = resultsByOrderFulfillment.some(
					(orderFulfillment) => orderFulfillment.isValid
				);
				const caloriesMatch = isOrderCalorieMatch(
					selectedPatientEhrOrder,
					calorie,
				);

				return {
					milkBottleId:
						item.type === "milkbottle" ? item.milkBottle.id : null,
					key: item.key,
					isValid: anyOrderFulfillmentValid && caloriesMatch,
					anyOrderFulfillmentValid,
					caloriesMatch,
					validationResultsByOrderFulfillment:
						resultsByOrderFulfillment,
				};
			});

		return resultsByFeedPatientListItem;
	}

	/**
	 * Validates bottle or prep products against each of the selected order's fulfillments.
	 *
	 * Uses the shared order-matching rules (validateContentProducts), the same as the scan
	 * page and the recipe calculators, so Manual Prep agrees with them: any water stands in
	 * for the order's water (AEP-2306), and EBM or DM stands in for the order's human milk
	 * only when the order's base allows that milk (AEP-2452).
	 */
	private validateSelectedOrderMatchesProducts(
		products: IContentProduct[],
		selectedOrder: PatientEhrOrderModel
	): FulfillmentValidationResult[] {
		// A bottle can hold the same product more than once (e.g. combined bottles); list each once.
		const contentProducts = _.chain(products)
			.filter((contentItem) => contentItem.productId != null)
			.uniqBy((contentItem) => contentItem.productId)
			.map((contentItem) => ({
				productId: contentItem.productId,
				productName: contentItem.productName,
			}))
			.value();

		return selectedOrder.fulfillments.map((ehrOrderFulfillment) =>
			validateContentProducts(
				contentProducts,
				ehrOrderFulfillment,
				selectedOrder.base
			)
		);
	}

	//--------------------------------------------------------------------------
	// Refactor of validateSelectedOrderMatchesProducts
	//--------------------------------------------------------------------------
	/**
	 * Get the fortifiers/additives and the base from the scanned feed objects
	 * in order to compare it with the fulfillment options for the selected
	 * order.
	 *
	 * Get validation results that contain various info about what's missing
	 * from the scanned feed objects.
	 *
	 * Return a combined results that contains more various info about what's
	 * missing from the scanned feed objects.
	 *
	 * @param selectedOrder
	 * @param calorieDensity
	 * @param additives
	 * @param recipeBase
	 * @private
	 */
	private _validateOrderMatchesRecipeCalculation(
		selectedOrder: PatientEhrOrderModel,
		calorieDensity: number,
		additives: IAdditive[],
		recipeBase: RecipeBase
	): IOrderValidationResult2[] {
		// If no order was selected because there are no orders, don't validate
		if (!selectedOrder) {
			return [];
		}

		// if (!hasMissingValue(selectedOrder, calorieDensity, additives)) {
		// 	return [];
		// }

		const products = createContentProductsList(additives, recipeBase);

		const results = selectedOrder.fulfillments.map((fulfillment) =>
			validateContentProducts(products, fulfillment, selectedOrder.base)
		);

		const hasValidFulfillment = results.some((v) => v.isValid);
		const caloriesMatch = isOrderCalorieMatch(selectedOrder, calorieDensity);

		/*
			The only difference between isValid and anyOrderFulfillmentValid is that
			isValid also checks if the calories match, which is kind of redundant.
		 */
		return [
			{
				milkBottleId: null, // why is this set to null?
				isValid: hasValidFulfillment && caloriesMatch, // TODO: remove this one
				anyOrderFulfillmentValid: hasValidFulfillment,
				caloriesMatch,
				validationResultsByOrderFulfillment: results,
			},
		];
	}
}

/**
 * @deprecated
 */
export interface IOrderValidationResult2 {
	milkBottleId?: string;
	key?: string; // unique id using nano id
	isValid: boolean;
	caloriesMatch: boolean;
	anyOrderFulfillmentValid: boolean;
	validationResultsByOrderFulfillment: Array<{
		ehrOrderFulfillmentId: string;
		milkBottleContentItemsNotInOrderFulfillment: {
			productId: Product["id"];
			productName: Product["name"];
		}[];
		baseOrderedIsPresent: boolean;
		orderFulfillmentNonBaseProductsNotInBottle: {
			productId: Product["id"];
			productName: Product["name"];
		}[];
		isValid: boolean;
	}>;
}

export interface IContentProduct {
	productId: Product["id"];
	productName: Product["name"];
}

export interface FulfillmentValidationResult {
	ehrOrderFulfillmentId: string;
	milkBottleContentItemsNotInOrderFulfillment: IContentProduct[];
	baseOrderedIsPresent: boolean;
	orderFulfillmentNonBaseProductsNotInBottle: IContentProduct[];
	isValid: boolean;
}
